import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from './Button';
import './SignUp.css';

function ProfilePictureUpload() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const apiUrl = process.env.REACT_APP_API_URL;

  const ensureAuthenticatedSession = async () => {
    const rememberToken = localStorage.getItem('rememberToken') || '';
    if (!rememberToken) return false;

    try {
      const response = await fetch(`${apiUrl}/check-auth.php`, {
        method: 'GET',
        headers: {
          'X-Remember-Token': rememberToken
        },
        credentials: 'include',
        cache: 'no-store'
      });
      const data = await response.json();
      return Boolean(response.ok && data?.valid);
    } catch (sessionError) {
      console.error('Session restore error:', sessionError);
      return false;
    }
  };

  useEffect(() => {
    const raw = localStorage.getItem('user');
    if (!raw) { navigate('/signup'); return; }
    const user = JSON.parse(raw);
    if (user.profilePicture) navigate('/dashboard');
  }, [navigate]);

  useEffect(() => {
    ensureAuthenticatedSession();
  }, []);

  useEffect(() => {
    document.body.style.background = 'linear-gradient(180deg, #930203 0%, #930203 50%, #ffffff 50%)';
    return () => { document.body.style.background = ''; };
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSelectedImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedImage) return;
    setLoading(true);
    setError('');

    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const sessionReady = await ensureAuthenticatedSession();
      if (!sessionReady) {
        setError('Your session expired. Please sign in again.');
        setLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('file', selectedImage);
      formData.append('userId', user?.id);
      const response = await fetch(`${apiUrl}/upload-profile-picture.php`, {
        method: 'POST',
        headers: {
          'X-Remember-Token': localStorage.getItem('rememberToken') || ''
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        const user = JSON.parse(localStorage.getItem('user'));
        const baseUrl = apiUrl.replace('/api', '');
        const profilePictureUrl = `${baseUrl}/${data.profilePicture}`;
        localStorage.setItem('user', JSON.stringify({ ...user, profilePicture: profilePictureUrl }));
        navigate('/dashboard');
      } else {
        setError(data.error || 'Upload failed. Please try again.');
      }
    } catch (err) {
      setError('Could not connect to server. Please check your connection.');
      console.error('Upload error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page" style={{
      background: 'linear-gradient(180deg, #930203 0%, #690202 100%)',
      minHeight: '100dvh',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      position: 'fixed',
    }}>
      <div className="sec-break" style={{ height: '100px' }}></div>

      <form onSubmit={handleSubmit} className="signup-modal d-flex flex-column justify-content-between">

        <div className="signup-top d-flex flex-column gap-2">

          <p className="signup-label mt-4">ACCOUNT SETUP</p>
          <h2 className="signup-heading serif-text fw-semibold">Add Profile Picture</h2>

          <div className="d-flex flex-column gap-2 mt-2">
            <div
              className="btn-neutral w-100 d-flex flex-column align-items-center justify-content-center"
              style={{ minHeight: '200px' }}
              onClick={() => fileInputRef.current.click()}
            >
              <span className="material-symbols-rounded" style={{ fontVariationSettings: '"FILL" 1', fontSize: '36px', color: '#6E0405' }}>
                add_a_photo
              </span>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>
            {selectedImage && (
              <p style={{ fontSize: '11px', opacity: 0.5, wordBreak: 'break-all', margin: 0 }}>
                {selectedImage.name}
              </p>
            )}
          </div>

          {error && (
            <div style={{
              color: '#930203',
              fontSize: '14px',
              backgroundColor: '#ffe6e6',
              padding: '10px',
              borderRadius: '5px',
              border: '1px solid #930203',
              marginTop: '10px',
            }}>
              {error}
            </div>
          )}
        </div>

        <div className="signup-bot mb-4">
          <Button variant="cta" type="submit" disabled={!selectedImage || loading}>
            {loading ? 'Uploading...' : 'Continue'}
          </Button>
        </div>

      </form>
    </div>
  );
}

export default ProfilePictureUpload;
